import type { SavedOutfit, ShowSummary } from '@runway/shared';
import { THEMES } from '@runway/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../net/api';
import { selectPendingInviteCount, useHub } from '../../state/hub';
import { useSession } from '../../state/session';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { cx } from '../../ui/cx';
import { UserAvatar } from '../friends/UserAvatar';
import { InvitesList } from './InvitesList';
import { StartShowWizard } from './StartShowWizard';
import { fetchMyShows, fetchOutfits, isUnavailable, showStatusMeta, type LoadState } from './api';
import './home.css';

function useLoader<T>(fn: () => Promise<T>, empty: T): LoadState<T> & { reload: () => void } {
  const [state, setState] = useState<LoadState<T>>({ data: empty, loading: true, error: null, unavailable: false });
  const reload = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    fn()
      .then((data) => setState({ data, loading: false, error: null, unavailable: false }))
      .catch((err: unknown) =>
        setState({ data: empty, loading: false, unavailable: isUnavailable(err), error: isUnavailable(err) ? null : err instanceof ApiError ? err.message : 'Could not load' }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fn]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { ...state, reload };
}

const GREETINGS = ['Ready to strut?', 'The runway is waiting.', 'Lights, camera, outfit.', 'Serve looks today.', 'Time to shine.'];

export function HomePage() {
  const user = useSession((s) => s.user);
  const [wizardOpen, setWizardOpen] = useState(false);
  const pendingInvites = useHub(selectPendingInviteCount);
  const shows = useLoader(fetchMyShows, [] as ShowSummary[]);
  const outfits = useLoader(fetchOutfits, [] as SavedOutfit[]);
  const [greeting] = useState(() => GREETINGS[Math.floor(Math.random() * GREETINGS.length)] ?? GREETINGS[0]);

  // Re-pull shows when an invite we accepted changes something, or when the hub reconnects.
  const status = useHub((s) => s.status);
  useEffect(() => {
    if (status === 'online') shows.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const liveShows = shows.data.filter((s) => showStatusMeta(s.status).live);

  return (
    <div className="stack" style={{ gap: 'var(--sp-8)' }}>
      <section className="home-hero" aria-labelledby="hero-title">
        <div>
          <div className="eyebrow">Backstage</div>
          <h1 id="hero-title">Hey {user?.displayName ?? 'star'} — {greeting}</h1>
          <p className="lede">Pick a theme, choose a judge, invite your friends and walk the runway. The judge scores every look — nicely.</p>
          <div className="home-hero-actions">
            <Button variant="primary" size="lg" onClick={() => setWizardOpen(true)}>
              ✨ Start a fashion show
            </Button>
            {liveShows[0] ? (
              <Link className="btn btn-gold btn-lg" to={`/show/${liveShows[0].id}`}>
                Rejoin “{themeTitle(liveShows[0].theme)}”
              </Link>
            ) : (
              <Link className="btn btn-ghost btn-lg" to="/wardrobe">
                Open wardrobe
              </Link>
            )}
          </div>
        </div>
        <HeroStage />
      </section>

      <div className="home-grid">
        <div className="stack" style={{ gap: 'var(--sp-6)' }}>
          <Card
            title="Your shows"
            actions={
              shows.data.length > 0 ? (
                <Button size="sm" variant="ghost" onClick={() => setWizardOpen(true)}>
                  New show
                </Button>
              ) : undefined
            }
          >
            <YourShows state={shows} onStart={() => setWizardOpen(true)} />
          </Card>

          <Card
            title="Saved looks"
            actions={
              <Link to="/wardrobe" className="small">
                Open wardrobe →
              </Link>
            }
          >
            <SavedLooks state={outfits} />
          </Card>
        </div>

        <div className="stack" style={{ gap: 'var(--sp-6)' }}>
          <Card title="Friends online">
            <FriendsOnline />
          </Card>
          <Card title="Invitations" actions={pendingInvites > 0 ? <span className="chip chip-gold">{pendingInvites} new</span> : undefined}>
            <InvitesList />
          </Card>
        </div>
      </div>

      <StartShowWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  );
}

// ── hero decoration ────────────────────────────────────────────────────────

function HeroStage() {
  return (
    <div className="hero-stage" aria-hidden="true">
      <div className="hero-spot left" />
      <div className="hero-spot right" />
      <div className="hero-catwalk" />
      <span className="hero-sparkle" style={{ top: '18%', left: '22%' }}>
        ✦
      </span>
      <span className="hero-sparkle" style={{ top: '30%', right: '18%', animationDelay: '-0.8s' }}>
        ✧
      </span>
      <span className="hero-sparkle" style={{ bottom: '28%', left: '36%', animationDelay: '-1.4s', fontSize: 12 }}>
        ✦
      </span>
    </div>
  );
}

// ── your shows ─────────────────────────────────────────────────────────────

function findTheme(theme: string) {
  return THEMES.find((t) => t.id === theme || t.title.toLowerCase() === theme.trim().toLowerCase());
}
function themeEmoji(theme: string): string {
  return findTheme(theme)?.emoji ?? '🎭';
}
/** shows.theme is a THEMES id for curated themes (custom text otherwise); show the title either way. */
function themeTitle(theme: string): string {
  return findTheme(theme)?.title ?? theme;
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function YourShows({ state, onStart }: { state: LoadState<ShowSummary[]> & { reload: () => void }; onStart: () => void }) {
  const me = useSession((s) => s.user?.id);
  const navigate = useNavigate();
  if (state.loading && state.data.length === 0) {
    return (
      <div className="stack stack-sm" aria-busy="true">
        {[0, 1].map((i) => (
          <div key={i} className="skeleton" style={{ height: 68 }} />
        ))}
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="empty" role="alert">
        <p className="help help-error">{state.error}</p>
        <Button size="sm" variant="ghost" onClick={state.reload}>
          Retry
        </Button>
      </div>
    );
  }
  if (state.data.length === 0) {
    return (
      <div className="empty">
        <div className="empty-emoji" aria-hidden="true">
          🎬
        </div>
        <h4>No shows yet</h4>
        <p>{state.unavailable ? 'Shows will appear here once the server is ready.' : 'Host your first one — it takes ten seconds to set up.'}</p>
        <Button variant="gold" size="sm" onClick={onStart}>
          Start a show
        </Button>
      </div>
    );
  }
  const recent = state.data.slice(0, 6);
  return (
    <div>
      {recent.map((s) => {
        const meta = showStatusMeta(s.status);
        const hosting = s.hostId === me;
        return (
          <div key={s.id} className="show-row">
            <div className="show-row-emoji" aria-hidden="true">
              {themeEmoji(s.theme)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="show-row-title">{themeTitle(s.theme)}</div>
              <div className="show-row-sub">
                <span className={cx('chip', `chip-${meta.tone}`, meta.live && 'chip-live')} style={{ minHeight: 22, padding: '0 8px', fontSize: 11 }}>
                  {meta.label}
                </span>
                <span>{hosting ? 'You hosted' : s.host ? `Hosted by ${s.host.displayName}` : ''}</span>
                <span>· {s.entrantCount} {s.entrantCount === 1 ? 'model' : 'models'}</span>
                <span>· {relTime(s.createdAt)}</span>
              </div>
            </div>
            <Button size="sm" variant={meta.live ? 'gold' : 'ghost'} onClick={() => navigate(`/show/${s.id}`)}>
              {meta.action}
            </Button>
          </div>
        );
      })}
      {state.data.length > recent.length ? <p className="small muted" style={{ marginTop: 'var(--sp-3)', textAlign: 'center' }}>Showing your {recent.length} most recent shows.</p> : null}
    </div>
  );
}

// ── friends online ─────────────────────────────────────────────────────────

function FriendsOnline() {
  const friends = useHub((s) => s.friends);
  const online = useHub((s) => s.online);
  const onlineFriends = useMemo(() => friends.filter((f) => online.has(f.id)), [friends, online]);
  const loaded = useHub((s) => s.loaded.friends);
  const openChat = useHub((s) => s.openChat);

  if (!loaded && friends.length === 0) {
    return <div className="skeleton" style={{ height: 84 }} aria-busy="true" />;
  }
  if (friends.length === 0) {
    return (
      <div className="empty">
        <div className="empty-emoji" aria-hidden="true">
          👯
        </div>
        <h4>No friends yet</h4>
        <p>Add one by name or email and they can join your shows.</p>
        <Link className="btn btn-gold btn-sm" to="/friends">
          Add a friend
        </Link>
      </div>
    );
  }
  if (onlineFriends.length === 0) {
    return (
      <div className="empty">
        <div className="empty-emoji" aria-hidden="true">
          🌙
        </div>
        <h4>Nobody is online right now</h4>
        <p>
          You have {friends.length} {friends.length === 1 ? 'friend' : 'friends'}. Invites still reach them the next time they sign in.
        </p>
      </div>
    );
  }
  return (
    <div className="online-strip" role="list" aria-label="Friends online">
      {onlineFriends.map((f) => (
        <button key={f.id} type="button" className="online-pill" role="listitem" onClick={() => openChat(f.id)} title={`Message ${f.displayName}`}>
          <UserAvatar displayName={f.displayName} picture={f.picture} avatar={f.avatar} size="lg" online />
          <span className="name">{f.displayName}</span>
        </button>
      ))}
    </div>
  );
}

// ── saved looks ────────────────────────────────────────────────────────────

function humanize(id: string): string {
  return id.replace(/-/g, ' ');
}

function lookColors(o: SavedOutfit['outfit']): string[] {
  const colors: string[] = [];
  if (o.dress) colors.push(o.dress.color, o.dress.accent ?? o.dress.color);
  else {
    if (o.top) colors.push(o.top.color);
    if (o.bottom) colors.push(o.bottom.color);
  }
  if (o.shoes) colors.push(o.shoes.color);
  for (const a of o.accessories.slice(0, 2)) colors.push(a.color);
  return colors.length ? colors : ['#2b1b4a'];
}

function lookCaption(o: SavedOutfit['outfit']): string {
  const parts: string[] = [];
  if (o.dress) parts.push(humanize(o.dress.id));
  else {
    if (o.top) parts.push(humanize(o.top.id));
    if (o.bottom) parts.push(humanize(o.bottom.id));
  }
  if (o.shoes) parts.push(humanize(o.shoes.id));
  if (o.accessories.length) parts.push(`${o.accessories.length} accessor${o.accessories.length === 1 ? 'y' : 'ies'}`);
  return parts.join(' · ') || 'A blank canvas';
}

function SavedLooks({ state }: { state: LoadState<SavedOutfit[]> & { reload: () => void } }) {
  const navigate = useNavigate();
  if (state.loading && state.data.length === 0) {
    return (
      <div className="looks-strip" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton" style={{ height: 120 }} />
        ))}
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="empty" role="alert">
        <p className="help help-error">{state.error}</p>
        <Button size="sm" variant="ghost" onClick={state.reload}>
          Retry
        </Button>
      </div>
    );
  }
  if (state.data.length === 0) {
    return (
      <div className="empty">
        <div className="empty-emoji" aria-hidden="true">
          👗
        </div>
        <h4>No saved looks yet</h4>
        <p>Build an outfit in the wardrobe and save it — you can load it in one tap during a show.</p>
        <Link className="btn btn-gold btn-sm" to="/wardrobe">
          Design a look
        </Link>
      </div>
    );
  }
  return (
    <div className="looks-strip" role="list" aria-label="Saved looks">
      {state.data.slice(0, 8).map((o) => (
        <button key={o.id} type="button" className="look-card" role="listitem" onClick={() => navigate('/wardrobe', { state: { outfitId: o.id } })} title={`Open “${o.name}” in the wardrobe`}>
          <div className="look-swatches" aria-hidden="true">
            {lookColors(o.outfit).map((c, i) => (
              <span key={i} style={{ background: c }} />
            ))}
          </div>
          <span className="look-name">{o.name}</span>
          <span className="look-sub">{lookCaption(o.outfit)}</span>
        </button>
      ))}
      <Link to="/wardrobe" className="look-card look-card-new" role="listitem">
        <span style={{ fontSize: 26 }} aria-hidden="true">
          ＋
        </span>
        New look
      </Link>
    </div>
  );
}
